import { ContractTypeProposalCard } from "@/apps/contract-review/components/contract-type-assistant/ContractTypeProposalCard";
import { ContractTypeResultCard } from "@/apps/contract-review/components/contract-type-assistant/ContractTypeResultCard";
import type { ContractTypeToolOutput } from "@/apps/contract-review/components/contract-type-assistant/types";
import { CustomRoleProposalCard } from "@/apps/contract-review/components/custom-role-assistant/CustomRoleProposalCard";
import { CustomRoleResultCard } from "@/apps/contract-review/components/custom-role-assistant/CustomRoleResultCard";
import type { CustomRoleToolOutput } from "@/apps/contract-review/components/custom-role-assistant/types";
import { isProgressLogComplete, isProgressLogError } from "@/lib/progress-log-icons";
import { cn } from "@/lib/utils";
import type { ProgressState } from "@/types";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { specialistForToolName } from "../specialists";
import { preferReviewQuestionSteps } from "../lib/review-question-steps";
import { QuestionFlow, type QuestionFlowStep } from "./elements/question-flow";
import { useReviewQuestion } from "./ReviewQuestionContext";
import { useSupervisorApproval } from "./SupervisorApprovalContext";

export function SupervisorWriteTool({
  toolName,
  toolCallId,
  args,
  result,
  isError,
}: {
  toolName: string;
  toolCallId: string;
  args: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
}) {
  const approval = useSupervisorApproval();
  const specialist = specialistForToolName(toolName);
  const mode = toolName.toLowerCase().includes("update") ? "update" : "create";

  if (result && typeof result === "object") {
    const output = result as ContractTypeToolOutput & CustomRoleToolOutput;
    if (specialist?.id === "custom-role" || "customRole" in output) {
      return (
        <CustomRoleResultCard
          mode={mode}
          output={output}
          errorText={isError ? output.error ?? "The custom role could not be saved." : undefined}
        />
      );
    }
    return (
      <ContractTypeResultCard
        mode={mode}
        output={output}
        errorText={isError ? output.error ?? "The contract type could not be saved." : undefined}
      />
    );
  }

  if (!specialist) return null;

  const pending = approval.activePending?.toolCallId === toolCallId ? approval.activePending : null;
  const proposal = (pending?.proposal ?? args) as Record<string, string | undefined>;
  const approvalBar =
    pending && approval.pendingDockActions
      ? approval.pendingDockActions
      : undefined;

  if (specialist.id === "custom-role") {
    return (
      <CustomRoleProposalCard
        mode={mode}
        proposal={proposal}
        disabled={approval.busy}
        showActions={Boolean(approvalBar)}
        approvalBar={approvalBar}
        onEditSubmit={(edited) => approval.approveEdited(toolCallId, edited)}
      />
    );
  }

  return (
    <ContractTypeProposalCard
      mode={mode}
      proposal={proposal}
      disabled={approval.busy}
      showActions={Boolean(approvalBar)}
      approvalBar={approvalBar}
      onEditSubmit={(edited) => approval.approveEdited(toolCallId, edited)}
    />
  );
}

function stepsFromArgs(args: Readonly<Record<string, unknown>> | undefined, fallback?: QuestionFlowStep[]) {
  return preferReviewQuestionSteps(args?.steps, fallback);
}

export function ReviewQuestionCard({
  toolCallId,
  args,
  result,
  fallbackRunId,
}: {
  toolCallId: string;
  args: Readonly<Record<string, unknown>>;
  result?: unknown;
  fallbackRunId?: string;
}) {
  const review = useReviewQuestion();
  const run = review.runs[toolCallId];
  const steps = stepsFromArgs(args, run?.steps);
  const answers =
    result && typeof result === "object" && "answers" in result
      ? (result.answers as Record<string, string[]>)
      : undefined;
  const errorText =
    result && typeof result === "object" && "error" in result && typeof result.error === "string"
      ? result.error
      : null;

  if (errorText && !answers) {
    return <p className="text-sm text-[#576274] dark:text-slate-300">{errorText}</p>;
  }

  return (
    <QuestionFlow
      steps={steps}
      choice={answers}
      onComplete={
        !answers
          ? async (next, extras) => {
              await review.submitAnswers(toolCallId, next, run?.runId ?? fallbackRunId, extras);
            }
          : undefined
      }
      completeLabel="Continue review"
    />
  );
}

type ActivityRow = {
  key: string;
  message: string;
  failed: boolean;
  complete: boolean;
};

function isStallMessage(message: string): boolean {
  return message.trim().toLowerCase().startsWith("still working on");
}

function activityRows(progress: ProgressState): ActivityRow[] {
  const rows: ActivityRow[] = [];
  progress.logs.forEach((log, index) => {
    if (isStallMessage(log.message)) return;
    const key = log.step && log.step.length > 0 ? log.step : `log-${index}`;
    const next: ActivityRow = {
      key,
      message: log.message,
      failed: isProgressLogError(log.step, log.message),
      complete: isProgressLogComplete(log.message),
    };
    const existing = log.step ? rows.find((row) => row.key === log.step) : undefined;
    if (existing) {
      existing.message = next.message;
      existing.failed = next.failed;
      existing.complete = next.complete;
      return;
    }
    rows.push(next);
  });
  return rows;
}

export function ReviewStepLog({ progress }: { progress: ProgressState }) {
  const [expanded, setExpanded] = useState(false);
  const rows = activityRows(progress);
  const settled = !progress.isConnected;
  const liveRow = rows[rows.length - 1];
  const live = Boolean(
    !settled && progress.isConnected && liveRow && !liveRow.failed && !liveRow.complete,
  );
  const canCollapse = !settled && rows.length > 4 && Boolean(liveRow);
  const hiddenCount = canCollapse ? rows.length - 1 : 0;
  const visible = canCollapse && !expanded && liveRow ? [liveRow] : rows;
  const doneCount = rows.filter((row) => !row.failed).length;

  if (rows.length === 0) return null;

  return (
    <div data-slot="review-step-log" className="flex flex-col gap-1.5 text-left">
      {settled ? (
        <p className="text-[13px] text-[#576274] dark:text-slate-300">
          {doneCount}/{rows.length}
        </p>
      ) : null}
      {hiddenCount > 0 ? (
        <button
          type="button"
          className="w-fit text-left text-[13px] text-[#576274] hover:text-[#101828] dark:text-slate-300 dark:hover:text-white"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Hide earlier steps" : `${hiddenCount} earlier steps`}
        </button>
      ) : null}
      {visible.map((row) => {
        const isLive = live && row.key === liveRow?.key;
        return (
          <div key={`${row.key}-${row.message}`} className="flex items-start gap-2">
            {row.failed ? (
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--tint-red-fg)] dark:text-red-400" />
            ) : isLive ? (
              <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-violet-500 dark:text-violet-300" />
            ) : (
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-300" />
            )}
            <span
              className={cn(
                "text-[13px] leading-relaxed",
                row.failed
                  ? "text-[var(--tint-red-fg)] dark:text-red-400"
                  : isLive
                    ? "text-[#101828] dark:text-white"
                    : "text-[#576274] dark:text-slate-300",
              )}
            >
              {row.message}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function ReviewProgressTool({ result }: { result?: unknown }) {
  const review = useReviewQuestion();
  const progress = review.reviewProgress;
  const failed =
    result && typeof result === "object" && "success" in result && result.success === false;
  const errorText =
    failed && "error" in result && typeof result.error === "string" ? result.error : null;
  const finished = result && typeof result === "object" && "reviewId" in result;
  const fileName =
    finished && "fileName" in result && typeof result.fileName === "string" ? result.fileName : "the contract";

  return (
    <div className="space-y-2">
      {errorText ? <p className="text-sm text-red-600 dark:text-red-300">{errorText}</p> : null}
      {finished ? (
        <p className="text-sm text-[#576274] dark:text-slate-300">
          Review of {fileName} is in the workspace on the right.
        </p>
      ) : null}
      {!errorText && !finished && !(progress && progress.logs.length > 0) ? (
        <p className="text-sm text-[#576274] dark:text-slate-300">Running the contract review…</p>
      ) : null}
    </div>
  );
}
